/**
 * ProactivePodiumCard — Feb 2026
 * ==============================
 *
 * Dashboard tile that surfaces the current week's Gold / Silver / Bronze
 * Proactive Champions to every teen. Reads the masked payload from
 * `GET /api/proactivity/podium?period=week` — no scores, no emails,
 * just first names + tier + place. Empty state before the master admin
 * has awarded the period, so nothing leaks mid-cycle.
 *
 * Weekly / Monthly toggle mirrors the Command Centre tile so a teen who
 * missed this week can still glance at the month's champions.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Trophy, Loader2, Sparkles, Award } from 'lucide-react';
import { apiClient } from '../../api/client';

const TIER_STYLE = {
  1: { bg: 'linear-gradient(135deg,#FEF3C7,#FDE68A)', border: '#F59E0B', ring: '#F59E0B', label: 'Gold',   emoji: '🥇' },
  2: { bg: 'linear-gradient(135deg,#F3F4F6,#E5E7EB)', border: '#9CA3AF', ring: '#9CA3AF', label: 'Silver', emoji: '🥈' },
  3: { bg: 'linear-gradient(135deg,#FEEBD7,#FED7AA)', border: '#C2410C', ring: '#C2410C', label: 'Bronze', emoji: '🥉' },
};

const ProactivePodiumCard = () => {
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiClient.request(`/api/proactivity/podium?period=${period}`);
      setData(r);
    } catch (e) {
      setError(e?.message || 'Failed to load podium');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const entries = data?.entries || [];
  // Pad podium visually to 3 slots so the layout is stable pre-reveal.
  const podiumSlots = [1, 2, 3].map((place) => {
    const found = entries.find(e => e.place === place);
    return { place, entry: found };
  });

  if (loading) {
    return (
      <div className="card" data-testid="proactive-podium-card">
        <div className="card-header">
          <h2 className="card-title">Proactive Champions</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" data-testid="proactive-podium-card">
        <div className="card-header">
          <h2 className="card-title">Proactive Champions</h2>
        </div>
        <p className="text-sm text-red-500 py-4">Failed to load champions</p>
      </div>
    );
  }

  return (
    <div className="card" data-testid="proactive-podium-card">
      <div className="card-header">
        <div className="flex items-center gap-2">
          <Trophy size={16} className="text-amber-500" />
          <h2 className="card-title">Proactive Champions</h2>
        </div>
        <div className="inline-flex rounded-full bg-gray-100 p-0.5" data-testid="podium-period-toggle">
          {['week', 'month'].map(p => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition ${
                period === p ? 'bg-amber-500 text-white shadow' : 'text-gray-600'
              }`}
              data-testid={`podium-period-${p}`}
            >
              {p === 'week' ? 'Week' : 'Month'}
            </button>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-gray-500 mb-3 -mt-1">{data?.period_label}</p>

      {entries.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Sparkles size={20} className="text-amber-400 mx-auto" />
          </div>
          <p className="empty-state-text">Champions revealed soon</p>
          <p className="text-xs text-gray-600 mt-2">
            Complete challenges, invite friends, help others, chat with Rebecca or share ideas — the podium drops at the end of the {period}.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2" data-testid="podium-grid">
          {podiumSlots.map(({ place, entry }) => {
            const style = TIER_STYLE[place];
            const empty = !entry;
            return (
              <div
                key={place}
                className="rounded-xl border-2 p-3 flex flex-col items-center text-center relative overflow-hidden"
                style={{
                  background: empty ? '#F9FAFB' : style.bg,
                  borderColor: empty ? '#E5E7EB' : style.border,
                  minHeight: 116,
                }}
                data-testid={`podium-slot-${place}`}
              >
                <div className="text-2xl mb-1" aria-hidden>{empty ? '–' : style.emoji}</div>
                {empty ? (
                  <>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      #{place}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">Pending</div>
                  </>
                ) : (
                  <>
                    <div
                      className="text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: style.border }}
                    >
                      #{entry.place} · {style.label}
                    </div>
                    <div className="text-sm font-bold text-gray-900 mt-1 leading-tight">
                      {entry.first_name}
                    </div>
                    <div
                      className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/70"
                      style={{ color: style.border }}
                    >
                      <Award size={10} /> {entry.nft_count} NFT{entry.nft_count !== 1 ? 's' : ''}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProactivePodiumCard;
